package com.anjuros.website.templates.template10.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.anjuros.website.components.language.LocalText
import com.anjuros.website.components.shared.AnjurosVideo
import com.anjuros.website.templates.template10.modules.Template10Theme
import com.anjuros.website.templates.template10.modules.getColor
import com.anjuros.website.templates.template10.styles.Template10TitleStyle
import com.anjuros.website.templates.template10.styles.template10Vertical
import com.anjuros.website.templates.template10.util.ResKey
import com.anjuros.website.templates.template10.util.Template10Constants
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextTransform
import com.varabyte.kobweb.compose.dom.svg.Circle
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.opacity
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textTransform
import com.varabyte.kobweb.compose.ui.modifiers.translateY
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.CSSColorValue
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Iframe
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.events.EventListener


/** Serif display font (Shippori Mincho) with fallbacks. */
fun Modifier.template10HeadingFont(): Modifier =
    this.fontFamily(Template10Constants.HEADING_FONT_FAMILY, "Georgia", "serif")

/** Body font (Manrope) with fallbacks. */
fun Modifier.template10BodyFont(): Modifier =
    this.fontFamily(Template10Constants.FONT_FAMILY, Template10Constants.FALLBACK_FONT, "sans-serif")


/** Red square "seal" holding a Chinese character. */
@Composable
fun Template10Seal(
    glyph: String,
    modifier: Modifier = Modifier,
    size: Int = 36,
    fontSize: Int = 17,
    vertical: Boolean = false,
    color: CSSColorValue = Template10Theme.Primary.getColor()
) {
    Box(
        modifier = modifier
            .then(if (vertical) Modifier.padding(leftRight = 8.px, topBottom = 14.px) else Modifier.size(size.px))
            .backgroundColor(color)
            .borderRadius(3.px),
        contentAlignment = Alignment.Center
    ) {
        Span(
            attrs = Modifier
                .template10HeadingFont()
                .fontSize(fontSize.px)
                .fontWeight(FontWeight.ExtraBold)
                .lineHeight(1.1)
                .color(Colors.White)
                .then(if (vertical) Modifier.template10Vertical().letterSpacing(0.2.em) else Modifier)
                .toAttrs()
        ) {
            Text(glyph)
        }
    }
}

/** Small uppercase label above section titles. */
@Composable
fun Template10Eyebrow(
    text: ResKey,
    color: CSSColorValue = Template10Theme.DeepRed.rgb,
    modifier: Modifier = Modifier
) {
    P(
        attrs = modifier
            .margin(0.px)
            .template10BodyFont()
            .fontSize(13.px)
            .fontWeight(FontWeight.Bold)
            .letterSpacing(0.16.em)
            .textTransform(TextTransform.Uppercase)
            .color(color)
            .toAttrs()
    ) {
        Text(LocalText(text))
    }
}

/** Large serif section title. */
@Composable
fun Template10Title(
    text: ResKey,
    color: CSSColorValue = Template10Theme.Ink.rgb,
    textAlign: TextAlign = TextAlign.Start,
    modifier: Modifier = Modifier
) {
    P(
        attrs = Template10TitleStyle.toModifier()
            .then(modifier)
            .margin(0.px)
            .template10HeadingFont()
            .fontWeight(FontWeight.Bold)
            .textAlign(textAlign)
            .color(color)
            .toAttrs()
    ) {
        Text(LocalText(text))
    }
}

/** Brush-like ink circle used behind the hero motto. */
@Composable
fun Template10InkCircle(
    modifier: Modifier = Modifier,
    color: String = Template10Theme.Paper.hex
) {
    Svg(
        attrs = modifier
            .fillMaxSize()
            .toAttrs {
                attr("viewBox", "0 0 200 200")
                attr("fill", "none")
                attr("aria-hidden", "true")
            }
    ) {
        Circle(attrs = {
            attr("cx", "100"); attr("cy", "100"); attr("r", "86")
            attr("stroke", color); attr("stroke-width", "9"); attr("stroke-linecap", "round")
            attr("stroke-dasharray", "470 70"); attr("transform", "rotate(-62 100 100)")
            attr("opacity", "0.92")
        })
        Circle(attrs = {
            attr("cx", "100"); attr("cy", "100"); attr("r", "80")
            attr("stroke", color); attr("stroke-width", "2"); attr("stroke-linecap", "round")
            attr("stroke-dasharray", "300 300"); attr("transform", "rotate(30 100 100)")
            attr("opacity", "0.4")
        })
    }
}

/** Responsive YouTube embed (16:9). In placeholder mode shows the video placeholder image. */
@Composable
fun Template10YouTube(
    videoId: String,
    modifier: Modifier = Modifier
) {
    AnjurosVideo(
        modifier = modifier
            .fillMaxWidth()
            .styleModifier {
                property("aspect-ratio", "16 / 9")
                property("object-fit", "cover")
            }
    ) {
        Box(
            modifier = modifier
                .fillMaxWidth()
                .styleModifier { property("aspect-ratio", "16 / 9") }
        ) {
            Iframe(
                attrs = Modifier
                    .fillMaxSize()
                    .toAttrs {
                        attr("src", "https://www.youtube-nocookie.com/embed/$videoId")
                        attr("title", "Tai Chi class video")
                        attr("frameborder", "0")
                        attr(
                            "allow",
                            "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        )
                        attr("allowfullscreen", "true")
                        attr("loading", "lazy")
                    }
            )
        }
    }
}


private var template10RevealCounter = 0

/** Subtle fade + slide-up played once when the content scrolls into view. */
@Composable
fun Template10Reveal(
    modifier: Modifier = Modifier,
    delayMs: Int = 0,
    offsetY: Int = 32,
    contentAlignment: Alignment = Alignment.TopStart,
    content: @Composable () -> Unit
) {
    val elementId = remember { "t10-reveal-${template10RevealCounter++}" }
    var visible by remember { mutableStateOf(false) }

    DisposableEffect(elementId) {
        val check: () -> Unit = {
            if (!visible) {
                val top = document.getElementById(elementId)?.getBoundingClientRect()?.top
                if (top != null && top < window.innerHeight * 0.9) {
                    visible = true
                }
            }
        }
        val listener = EventListener { check() }
        window.addEventListener("scroll", listener)
        window.addEventListener("resize", listener)
        val timeout = window.setTimeout({ check() }, 120)

        onDispose {
            window.clearTimeout(timeout)
            window.removeEventListener("scroll", listener)
            window.removeEventListener("resize", listener)
        }
    }

    Box(
        modifier = modifier
            .id(elementId)
            .opacity(if (visible) 1f else 0f)
            .translateY(if (visible) 0.px else offsetY.px)
            .styleModifier {
                property(
                    "transition",
                    "opacity 900ms ease ${delayMs}ms, translate 900ms cubic-bezier(0.22, 1, 0.36, 1) ${delayMs}ms"
                )
            },
        contentAlignment = contentAlignment
    ) {
        content()
    }
}
